import React, { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  TaskAlt,
  Refresh,
  Error as ErrorIcon,
  CameraAltOutlined,
  Add,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Photos, Project, ZenitalCamera } from '../types';
import PhotoGallery from './PhotoGallery';
import { defaultRetryPolicy } from '../utils/retryPolicy';
import { analyzeImageQuality, QualityReport } from '../functions/qualityCheck';
import { detectMarker, MarkerDetectionResult } from '../functions/markerDetector';
import { getDeviceInfoForBackend } from '../utils/deviceInfo';
import { captureException } from '../utils/captureException';

interface PhotoButtonProps {
  type: string;
  status: string;
  warning: boolean;
  label: string;
  id: string;
  setError: (error: string | null) => void;
  project: Project;
  refetch: () => void;
  photos: Photos[];
  isMobile: boolean;
  mandatory?: boolean;
  multiple?: boolean;
  setProject: (project: Project) => void;
  globalDisabled: boolean;
  setGlobalDisabled: (globalDisabled: boolean) => void;
  doQualityCheck: boolean;
  zenitalCamera?: ZenitalCamera;
}

const PhotoButton: React.FC<PhotoButtonProps> = ({
  type,
  status,
  warning,
  label,
  id,
  setError,
  project,
  refetch,
  photos,
  isMobile,
  mandatory = false,
  multiple = false,
  setProject,
  globalDisabled = false,
  setGlobalDisabled,
  doQualityCheck,
  zenitalCamera,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isCompleted = status === 'completed';
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [qualityCheck, setQualityCheck] = useState<{
    open: boolean;
    report: QualityReport | null;
    file: File | null;
  }>({
    open: false,
    report: null,
    file: null,
  });
  const [zenitalDialogOpen, setZenitalDialogOpen] = useState(false);
  const [zenitalLoading, setZenitalLoading] = useState(false);

  const isEditable = project?.editable !== false;
  
  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
  
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      setError(t('errors.fileSelection.invalidFileType'));
      return;
    }
  
    setProcessing(true);
    setGlobalDisabled(true);
    setError(null);
    
    try {
      // First, analyze image quality
      console.log('analyzing image quality');
      if (doQualityCheck) {
        const qualityReport = await analyzeImageQuality(file);
        console.log('qualityReport: ', qualityReport);
        setProcessing(false);
        setGlobalDisabled(false);
        
        // If quality is bad, prevent API call and show warning
        if (qualityReport.summary === 'bad') {
          setQualityCheck({
            open: true,
            report: qualityReport,
            file: file,
          });
          return;
        }
        
        // If quality is OK/warn or good, proceed with upload
        if (qualityReport.summary === 'ok') {
          setQualityCheck({
            open: true,
            report: qualityReport,
            file: file,
          });
          return; // User can decide to proceed or retake
        }
      } else {
        setProcessing(false);
        setGlobalDisabled(false);
      }
      // For good quality, proceed directly
      await proceedWithUpload(file);
      
    } catch (err) {
      captureException(err, { tags: { feature: 'photoButton' }, extra: { action: 'analyzeImageQuality', zoneId: id } });
      console.error('Error analyzing image quality:', err);
      setError(t('errors.processing.failedAnalyzeQuality'));
      setProcessing(false);
      setGlobalDisabled(false);
    }
  };

  const generateCameraId = async (): Promise<string> => {
    const deviceInfo = getDeviceInfoForBackend();
    const deviceString = `${deviceInfo.agent}_${deviceInfo.brand}_${deviceInfo.model}_${deviceInfo.platform}_${deviceInfo.screenWidth}_${deviceInfo.screenHeight}`;
    const encoder = new TextEncoder();
    const data = encoder.encode(deviceString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex.substring(0, 32);
  };

  const proceedWithUpload = async (file: File) => {
    setProcessing(true);
    // setGlobalDisabled(true);
    setError(null);
    
    try {
      // Process image with marker detection before uploading
      let markerResult: MarkerDetectionResult | null = null;
      if (['Trasera', 'Delantera', 'Derecha', 'Izquierda'].includes(id) && project.is_kit !== true) {
        markerResult = await detectMarker(file);
      } 
      console.log('markerResult: ', markerResult);

      if (!user) throw new Error(t('errors.processing.userNotAuthenticated'));
      const idToken = await user.getIdToken();

      const formData = new FormData();
      formData.append('image', file, file.name);
      formData.append('userId', user.uid);
      const cameraId = await generateCameraId();
      formData.append('camera_id', cameraId);
      const referencePoints = JSON.stringify({
        imageInfo: {
          height: markerResult?.height,
          width: markerResult?.width,
          size_reference_mm: 200,
        },
        points: markerResult?.corners,
        userAgent: navigator.userAgent,
      });
      formData.append('reference_points', referencePoints);
      let apiUrl = "";
      if (type === 'ficha' || type === 'chasis' || type === 'xapa') {
        apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/extract/image?group=${type}&project_code=${project.project_code}`;
      } else {
        apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/predict/image?zone=${id}&project_code=${project.project_code}&multiple=${multiple}`;
      }

      console.log("API URL", apiUrl);

      // No retry: this POST is long-running and not idempotent. A blind retry can
      // start a second request for the same zone while the first is still processing.
      const response = await axios.post<{ id: string }>(apiUrl, formData, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });

      if (response.data) {
        // Navigate to the newly created prediction
        console.log(response.data);
        await refetch();
        setProcessing(false);
        // setGlobalDisabled(false);
        // window.location.href = `/prediction/${response.data.id}`;
      } else {
        throw new Error(t('errors.processing.noResultId'));
      }
    } catch (err) {
      captureException(err, { tags: { feature: 'photoButton' }, extra: { action: 'proceedWithUpload', zoneId: id, project_code: project?.project_code } });
      console.error('Error sending image:', err);
      let errorMessage = t('errors.processing.failed');
      if (axios.isAxiosError(err) && err.response) {
        errorMessage = `Error: ${err.response.data?.details || err.response.data?.error || err.message}`;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setProcessing(false);
      // setGlobalDisabled(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };


  const handleQualityCheckClose = () => {
    setQualityCheck({ open: false, report: null, file: null });
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleProceedWithQuality = async () => {
    if (qualityCheck.file) {
      handleQualityCheckClose();
      await proceedWithUpload(qualityCheck.file);
    }
  };

  const handleViewPhoto = () => {
    setGalleryOpen(true);
  };

  const handleRetakePhoto = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent parent click
    if (!isEditable) return;
    if (type === 'techo') {
      handleZenitalConfirm();
      // setZenitalDialogOpen(true);
    } else {
      cameraInputRef.current?.click();
    }
  };

  const handleAddPhoto = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent parent click
    if (!isEditable) return;
    console.log('Add photo button clicked for zone:', id, 'type:', type);
    if (type === 'techo') {
      handleZenitalConfirm();
      // setZenitalDialogOpen(true);
    } else {
      cameraInputRef.current?.click();
    }
  };

  const handlePhotoButtonClick = (event: React.MouseEvent<HTMLDivElement>, photoType: string) => {
    if (processing || globalDisabled) {
      return; // Disable clicks when processing
    }

    if (!isEditable && !isCompleted) return;
    
    if (status === 'completed') {
      handleViewPhoto();
    } else {
      if (photoType === 'techo') {
        handleZenitalConfirm();
        // setZenitalDialogOpen(true);
      } else {
        cameraInputRef.current?.click();
      }
    }
  };

  const getZenitalCameraAccessToken = async () => {
    try {
      const body = new URLSearchParams({
        username: zenitalCamera?.username || '',
        password: zenitalCamera?.password || ''
      });
      const protocol = zenitalCamera?.protocol || 'http';
      const apiUrl = `${protocol}://${zenitalCamera?.ip}:${zenitalCamera?.port}/token?key=${zenitalCamera?.key}`;
      console.log('apiUrl: ', apiUrl);
      const response = await defaultRetryPolicy.executeWithRetry(async () => {
        return await axios.post(apiUrl, body, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          }
        });
      }, 'Zenital Camera Image API token request', { url: apiUrl, method: 'POST' });

      if (response.status !== 200) {
        throw new Error(`Authentication failed: ${response.status} ${response.statusText}`);
      }

      const tokenData = response.data;
      
      const accessToken = tokenData.access_token;
      
      return accessToken;
    } catch (error) {
      console.error('Error authenticating with Pervasive Take Zenital Photo API:', error);
      setError(t('errors.processing.failed'));
      return null;
    }
  }

  const takeZenitalPhoto = async () => {
    const protocol = zenitalCamera?.protocol || 'http';
    const url = `${protocol}://${zenitalCamera?.ip}:${zenitalCamera?.port}/image?key=${zenitalCamera?.key}`;
    try {
      const token = await getZenitalCameraAccessToken();
      if (!token) return;
      const header = {
        'X-Forwarded-Authorization': token,
      };

      const response = await fetch(url, {
        headers: header,
      });

      if (!response.ok) {
        const error = new Error(`Zenital Camera Image API error: ${response.status} - ${response.statusText}`);
        setError(t('errors.processing.failed') + ' - ' + error.message);
        return null;
      }
      return response.arrayBuffer();
    } catch (error) {
      captureException(error, { tags: { feature: 'photoButton' }, extra: { action: 'takeZenitalPhoto', project_code: project?.project_code } });
      console.error('Error taking zenital photo:', error);
      setError(t('errors.processing.failed') + ' ' + (error instanceof Error ? error.message : ' - Unknown error'));
      return null;
    }
  }

  const requestZenitalPhoto = async () => {
    try {
      setProcessing(true);
      setGlobalDisabled(true);
      setError(null);

      const imageBuffer = await takeZenitalPhoto();
      setZenitalLoading(false);
      setZenitalDialogOpen(false);

      if (!imageBuffer) return;
      
      const contentType = 'image/jpeg';
      const blob = new Blob([imageBuffer], { type: contentType });
      const file = new File([blob], `zenital_${Date.now()}.jpg`, { type: contentType });
      

      console.log('file: ', file);
      await proceedWithUpload(file);
    } catch (err: any) {
      console.error('Error getting zenital photo:', err);
      let errorMessage = t('errors.processing.failed');
      if (axios.isAxiosError(err) && err.response) {
        errorMessage = `Error: ${err.response.data?.details || err.response.data?.error || err.message}`;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setProcessing(false);
      setGlobalDisabled(false);
    }
  };

  const handleZenitalConfirm = async () => {
    setZenitalLoading(true);
    await requestZenitalPhoto();
  };

  const handleZenitalCancel = () => {
    setZenitalDialogOpen(false);
    cameraInputRef.current?.click();
  };

  useEffect(() => {
    if (photos?.length === 0 || photos === undefined) {
      setGalleryOpen(false);
    }
  }, [photos]);
  
  return (
    <>
      <Paper
        key={type}
        elevation={0}
        sx={{
          border: '2px dashed',
          borderColor: processing ? 'warning.light' : isCompleted ? 'success.light' : 'primary.light',
          borderRadius: 2,
          p: 2,
          backgroundColor: processing ? 'warning.extraLight' : isCompleted ? 'success.extraLight' : 'primary.extraLight',
          cursor: processing ? 'auto' : globalDisabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: processing ? 'warning.main' : isCompleted ? 'success.main' : 'primary.main',
            backgroundColor: processing ? 'warning.extraLight' : isCompleted ? 'success.light' : 'primary.light',
            transform: 'translateY(-1px)',
          },
        }}
        onClick={(event) => handlePhotoButtonClick(event, type)}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              color: processing ? 'warning.main' : isCompleted ? 'success.main' : 'primary.main',
            }}
          >
            <CameraAltOutlined sx={{ color: (processing ? 'warning.main' : isCompleted ? 'success.main' : mandatory ? 'primary.main' : 'warning.main') }} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: processing ? 'warning.main' : isCompleted ? 'success.main' : 'primary.main',
              }}
            >
              {label}
            </Typography>
          </Box>
          <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
          {isEditable && isCompleted && !processing && !warning && (
            <>
              {multiple && photos.length > 0 ? (
                <Tooltip title={t('photoButton.addPhoto')}>
                  <IconButton
                    onClick={handleAddPhoto}
                    sx={{ color: 'success.main', p: 0, mr: 1, height: 24, width: 24 }}
                  >
                    <Add sx={{ color: 'success.main' }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t('photoButton.retakePhoto')}>
                  <IconButton
                    onClick={handleRetakePhoto}
                    sx={{ color: 'success.main', p: 0, mr: 1, height: 24, width: 24 }}
                  >
                    <Refresh sx={{ color: 'success.main' }} />
                  </IconButton>
                </Tooltip>
              )}
              <TaskAlt sx={{ color: 'success.main' }} />
            </>
            )}
          {processing && (
            <CircularProgress size={20} sx={{ color: 'warning.main' }} />
          )}
          {isCompleted && !processing && warning && (
            <>
              {isEditable && (
                multiple && photos.length > 0 ? (
                  <Tooltip title={t('photoButton.addPhoto')}>
                    <IconButton
                      onClick={handleAddPhoto}
                      sx={{ color: 'success.main', p: 0, mr: 1, height: 24, width: 24 }}
                    >
                      <Add sx={{ color: 'success.main' }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('photoButton.retakePhoto')}>
                    <IconButton
                      onClick={handleRetakePhoto}
                      sx={{ color: 'success.main', p: 0, mr: 1, height: 24, width: 24 }}
                    >
                      <Refresh sx={{ color: 'success.main' }} />
                    </IconButton>
                  </Tooltip>
                )
              )}
              <Tooltip title={t('photoButton.imageNotMatching')}>
                <ErrorIcon sx={{ color: 'warning.main' }} />
              </Tooltip>
            </>
          )}
          </Box>
        </Box>
      </Paper>

      <input
        type="file"
        accept="image/*"
        hidden
        ref={cameraInputRef}
        onChange={handleFileSelected}
        onClick={(event) => event.stopPropagation()}
        disabled={!isEditable}
        capture="environment"
      />


      <PhotoGallery
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        photos={photos || []}
        initialPhotoType={type}
        isMobile={isMobile}
        multiple={multiple}
        mandatory={mandatory}
        onPhotoDeleted={async () => {
          // Refetch project data after photo is added
          try {
            const idToken = await user?.getIdToken();
            if (!idToken) return;
            
            const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
              headers: { 'Authorization': `Bearer ${idToken}` },
            });
            
            if (response.data) {
              setProject(response.data);
              console.log("setting data", response.data);
            }
          } catch (err) {
            captureException(err, { tags: { feature: 'photoButton' }, extra: { action: 'refetchProject', zoneId: id, project_code: project?.project_code } });
            console.error('Error refetching project:', err);
            // Don't clear photos on error - keep the existing ones
          }
        }}
      />

      {/* Quality Check Dialog */}
      <Dialog
        open={qualityCheck.open}
        onClose={handleQualityCheckClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('qualityCheck.title')}
        </DialogTitle>
        <DialogContent>
          {qualityCheck.report && (
            <Box sx={{ mb: 2 }}>
              <Alert 
                severity={qualityCheck.report.summary === 'bad' ? 'error' : 'warning'}
                sx={{ borderRadius: '0px !important', mb: 2 }}
              >
                <Typography variant="body2">
                  {qualityCheck.report.summary === 'bad'
                    ? t('qualityCheck.poorQuality', { score: qualityCheck.report.score })
                    : t('qualityCheck.acceptableQuality', { score: qualityCheck.report.score })
                  }
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {isEditable && (
            <Button onClick={handleQualityCheckClose} color="primary">
              {t('qualityCheck.retakePhoto')}
            </Button>
          )}
          {isEditable && qualityCheck.report && qualityCheck.report.summary !== 'bad' && (
            <Button onClick={handleProceedWithQuality} variant="contained" color="primary">
              {t('qualityCheck.useAnyway')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Zenital camera choice */}
      <Dialog open={zenitalDialogOpen} onClose={() => setZenitalDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('photoTaker.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('photoTaker.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleZenitalCancel} disabled={zenitalLoading}>
           {t('photoTaker.useDevice')}
          </Button>
          <Button onClick={handleZenitalConfirm} variant="contained" color="primary" disabled={zenitalLoading}>
            {zenitalLoading ? <CircularProgress size={18} /> : t('photoTaker.useZenitalCamera')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PhotoButton;
